import { skipToken } from "@reduxjs/toolkit/query";
import {
  CustomFieldResourceType,
  type CustomFieldDefinition,
} from "@repo/app-types/entities/customField";
import { MultiSelectMenuPopover } from "@repo/ui/Menu/multiSelectMenuPopover";
import type { MultiSelectSection } from "@repo/ui/Select/multiSelect";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { MenuTrigger } from "react-aria-components";
import { useAllDataExtractionFieldDefinitionsQuery } from "../../../api/entities/dataExtractionField.js";
import { useUpdateTeammateMutation } from "../../../api/entities/teammate.js";
import { useAllUserCustomFieldDefinitionsQuery } from "../../../api/entities/userCustomField.js";
import { useAppSelector } from "../../../hooks/hook.js";
import { selectTeammate } from "../../teammate/teammateSlice.js";
import { DisplayColumnsMenuTrigger } from "../displayColumnsMenuTrigger.js";

type PreferenceScope =
  | { type: "default" }
  | { type: "teammateAgent"; teammateAgentId: string };

interface UseCustomFieldColumnSelectorOptions {
  includeDataExtractionFields?: boolean;
  preferenceScope: PreferenceScope;
}

interface UseCustomFieldColumnSelectorReturn {
  selectedCustomFieldDefinitions: readonly CustomFieldDefinition[];
  selector: ReactNode;
  /** The MultiSelect sections data for combining with other selectors */
  selectSections: readonly MultiSelectSection[];
  /** The currently selected IDs for combining with other selectors */
  selectedIds: readonly string[];
  /** The onChange handler for combining with other selectors */
  onSelectionChange: (keys: readonly string[]) => void;
}

/**
 * Hook that manages custom field column selection state and provides a dropdown UI.
 * Consolidates logic for fetching custom fields, tracking selections, and rendering the selector.
 */
export function useCustomFieldColumnSelector(
  options: UseCustomFieldColumnSelectorOptions,
): UseCustomFieldColumnSelectorReturn {
  const currentTeammate = useAppSelector(selectTeammate);
  const [updateTeammate] = useUpdateTeammateMutation();

  const [selectedIds, setSelectedIds] = useState<readonly string[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);

  const { data: customFieldDefinitions, isLoading: isLoadingUserFields } =
    useAllUserCustomFieldDefinitionsQuery();

  const {
    data: dataExtractionFieldDefinitions,
    isLoading: isLoadingDataExtractionFields,
  } = useAllDataExtractionFieldDefinitionsQuery(
    options.includeDataExtractionFields ? undefined : skipToken,
  );

  const isLoading = isLoadingUserFields || isLoadingDataExtractionFields;

  // Filter and group custom fields by resource type
  const contactCustomFields = (customFieldDefinitions ?? []).filter(
    (field) => field.resource_type === CustomFieldResourceType.CONTACT,
  );

  const accountCustomFields = (customFieldDefinitions ?? []).filter(
    (field) => field.resource_type === CustomFieldResourceType.ACCOUNT,
  );

  const gradingDataExtractionFields = useMemo(
    () =>
      options.includeDataExtractionFields
        ? dataExtractionFieldDefinitions?.filter(
            (field) => field.resource_type === CustomFieldResourceType.GRADING,
          )
        : undefined,
    [options.includeDataExtractionFields, dataExtractionFieldDefinitions],
  );

  // Load saved preferences on mount
  useEffect(() => {
    if (!currentTeammate || isInitialized || isLoading) {
      return;
    }

    const savedPreferences =
      currentTeammate.settings.preferences
        .team_performance_custom_field_column_ids;

    if (!savedPreferences) {
      setIsInitialized(true);
      return;
    }

    let savedIds: string[] | null = null;

    if (options.preferenceScope.type === "default") {
      savedIds = savedPreferences.default;
    } else if (options.preferenceScope.type === "teammateAgent") {
      savedIds =
        savedPreferences.teammate_agent?.[
          options.preferenceScope.teammateAgentId
        ] ?? null;
    }

    if (savedIds) {
      // Filter out any invalid IDs (fields that no longer exist)
      const allDefinitions = [
        ...(customFieldDefinitions ?? []),
        ...(gradingDataExtractionFields ?? []),
      ];
      const validIds = savedIds.filter((id) =>
        allDefinitions.some((field) => field.id === id),
      );
      setSelectedIds(validIds);
    }

    setIsInitialized(true);
  }, [
    currentTeammate,
    customFieldDefinitions,
    gradingDataExtractionFields,
    isInitialized,
    isLoading,
    options.preferenceScope,
  ]);

  const selectedCustomFieldDefinitions = useMemo(() => {
    const allDefinitions = [
      ...(customFieldDefinitions ?? []),
      ...(gradingDataExtractionFields ?? []),
    ];

    return allDefinitions.filter((field) => selectedIds.includes(field.id));
  }, [customFieldDefinitions, gradingDataExtractionFields, selectedIds]);

  const handleSelectionChange = async (keys: readonly string[]) => {
    setSelectedIds(keys);

    // Persist to backend
    if (currentTeammate) {
      try {
        const currentPreferences = currentTeammate.settings.preferences
          .team_performance_custom_field_column_ids ?? {
          default: null,
          teammate_agent: null,
        };

        const updatedPreferences =
          options.preferenceScope.type === "default"
            ? {
                ...currentPreferences,
                default: [...keys],
              }
            : {
                ...currentPreferences,
                teammate_agent: {
                  ...(currentPreferences.teammate_agent ?? {}),
                  [options.preferenceScope.teammateAgentId]: [...keys],
                },
              };

        await updateTeammate({
          id: currentTeammate.id,
          patchPayload: {
            preferences: {
              team_performance_custom_field_column_ids: updatedPreferences,
            },
          },
        }).unwrap();
      } catch (error) {
        console.error("Failed to save column preference:", error);
      }
    }
  };

  // Build sections for MultiSelect
  const sections: MultiSelectSection[] = [];

  if (
    gradingDataExtractionFields !== undefined &&
    gradingDataExtractionFields.length > 0
  ) {
    sections.push({
      id: "grading",
      label: "Data extraction fields",
      options: gradingDataExtractionFields.map((field) => ({
        id: field.id,
        label: field.display_name,
      })),
    });
  }

  if (contactCustomFields.length > 0) {
    sections.push({
      id: "contact",
      label: "Contact custom fields",
      options: contactCustomFields.map((field) => ({
        id: field.id,
        label: field.display_name,
      })),
    });
  }

  if (accountCustomFields.length > 0) {
    sections.push({
      id: "account",
      label: "Account custom fields",
      options: accountCustomFields.map((field) => ({
        id: field.id,
        label: field.display_name,
      })),
    });
  }

  const hasCustomFields = sections.length > 0;

  const selector = hasCustomFields ? (
    <MenuTrigger>
      <DisplayColumnsMenuTrigger />
      <MultiSelectMenuPopover
        ariaLabel="Display columns"
        placement="bottom end"
        sections={sections}
        value={selectedIds}
        onChange={handleSelectionChange}
      />
    </MenuTrigger>
  ) : null;

  return {
    selectedCustomFieldDefinitions,
    selector,
    selectSections: sections,
    selectedIds,
    onSelectionChange: handleSelectionChange,
  };
}
